import { Check, CircleAlert, LoaderCircle, Send } from 'lucide-react';
import { ForestLengthGroup, ForestTodayButton, PageHeader } from '@/shared/components';
import { cn } from '@/shared/lib/cn';
import {
  AVAILABILITY_ZOOMS,
  type AvailabilityTab,
  type AvailabilityZoom,
} from './availabilitySearch';
import { AvailabilityStartDate } from './AvailabilityStartDate';
import { transmissionSentence, type TransmissionSummary } from './transmissionStatus';

interface Props {
  tab: AvailabilityTab;
  onTabChange: (tab: AvailabilityTab) => void;
  summary: TransmissionSummary | null;
  from: string;
  to: string;
  days: AvailabilityZoom;
  onFromChange: (iso: string) => void;
  onDaysChange: (days: AvailabilityZoom) => void;
  onToday: () => void;
}

const TABS: [AvailabilityTab, string][] = [
  ['calendar', 'Grundkalender'],
  ['channels', 'Kanalregeln'],
];

/**
 * Wald-Kopf von Verfügbarkeit & Vertrieb (Board „Vertrieb“): Pfad und Bereichsumschalter aus dem
 * Gerüst, Titel mit dem Übertragungsstand, die Zeitraum-Werkzeuge im Grundkalender und die Reiter
 * Grundkalender · Kanalregeln. Kanalregeln sieht jeder mit Ansehen-Recht, ändern nur mit Full.
 */
export function AvailabilityHeader(props: Props) {
  const { summary } = props;
  return (
    <PageHeader
      className="pb-[84px] md:pb-[84px]"
      title="Verfügbarkeit & Vertrieb"
      description={summary ? <TransmissionChip summary={summary} /> : undefined}
      tools={
        props.tab === 'calendar' ? (
          <>
            <AvailabilityStartDate
              from={props.from}
              to={props.to}
              onFromChange={props.onFromChange}
              onPrevious={() => props.onFromChange(shift(props.from, -props.days))}
              onNext={() => props.onFromChange(shift(props.from, props.days))}
            />
            <ForestTodayButton onToday={props.onToday} />
            <ForestLengthGroup
              values={AVAILABILITY_ZOOMS}
              value={props.days}
              onChange={props.onDaysChange}
            />
          </>
        ) : undefined
      }
      filterTabs={TABS.map(([tab, label]) => ({
        label,
        active: props.tab === tab,
        onSelect: () => props.onTabChange(tab),
      }))}
      filterTabsKind="tabs"
      filterTabsLabel="Bereich"
    />
  );
}

function shift(iso: string, days: number): string {
  const date = new Date(`${iso}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

const ICON = {
  confirmed: Check,
  sent: Send,
  running: LoaderCircle,
  failed: CircleAlert,
  none: Send,
};

/** Ein Satz, der zusammenfasst – die Wörter je Übertragung stehen im Apartment-Reiter. */
export function TransmissionChip({ summary }: { summary: TransmissionSummary }) {
  // Veraltet dreht nichts und färbt nichts grün: der Satz beschreibt den zuletzt geladenen Stand.
  const Icon = summary.stale ? CircleAlert : ICON[summary.tone];
  return (
    <span
      role="status"
      className={cn(
        'rounded-pill inline-flex min-h-[30px] items-center gap-1.5 px-3 text-xs font-bold text-white',
        summary.stale ? 'bg-white/10' : summary.tone === 'failed' ? 'bg-sealwax/40' : 'bg-mint/20',
      )}
    >
      <Icon
        size={13}
        aria-hidden
        className={cn(
          summary.stale
            ? 'text-amber-on-dark'
            : summary.tone === 'failed'
              ? 'text-sealwax-on-dark'
              : 'text-mint',
          summary.tone === 'running' && !summary.stale && 'motion-safe:animate-spin',
        )}
      />
      {transmissionSentence(summary)}
    </span>
  );
}
